import LogApi from '@/api/common/log';
import {BANNER_RESPONSE_TYPE} from '@/constants/main/banner';
import useMainStore from '@/stores/main';
import VastInfo from '@/utils/player/VastInfo';
import {useEffect, useRef, useState} from 'react';
import {useBanner} from '../main/common/hooks/useBanner';
import {BannerAdPlayer} from '../player/banner/BannerAdPlayer';
import MainBannerCss from '/public/static/css/components/mainBanner.module.scss';
import player from '/public/static/css/components/player.module.scss';
import subBannerCss from '/public/static/css/components/subBanner.module.scss';
import '/public/static/css/module/ad_player.scss';

export default function Banner({
  type = '',
  category = '',
  posterCategory = {active: '', categoryNo: ''},
}) {
  const {
    isOpen,
    banner,
    handleClickClose,
    handleClickBanner,
    handelErrorImage,
    handleMouseEnter,
    handleMouseLeave,
  } = useBanner({type, category, posterCategory});

  return type === 'main' ? (
    <MainBannerComponent
      bannerData={banner.main}
      handleClickBanner={handleClickBanner}
      handelErrorImage={handelErrorImage}
    />
  ) : (
    <SubBannerComponent
      isOpen={isOpen}
      bannerData={banner.sub}
      handleClickBanner={handleClickBanner}
      handleClickClose={handleClickClose}
      handelErrorImage={handelErrorImage}
      handleMouseEnter={handleMouseEnter}
      handleMouseLeave={handleMouseLeave}
    />
  );
}

const MainBannerComponent = ({
  bannerData,
  handelErrorImage,
  handleClickBanner,
}) => {
  if (bannerData.exist) {
    switch (bannerData.type) {
      case BANNER_RESPONSE_TYPE.PC_MAIN_IMAGE:
        return (
          <ImageMainBannerComponent
            bannerData={bannerData}
            handleClickBanner={handleClickBanner}
            handelErrorImage={handelErrorImage}
          />
        );
      case BANNER_RESPONSE_TYPE.PC_MAIN_VIDEO:
        return (
          <VideoMainBannerComponent
            bannerData={bannerData}
            handleClickBanner={handleClickBanner}
          />
        );
      default:
        return null;
    }
  }
};

const ImageMainBannerComponent = ({
  bannerData,
  handelErrorImage,
  handleClickBanner,
}) => {
  const {
    checkAd,
    isShowMainBanner,
    isCloseMainBanner,
    setIsCloseMainBanner,
    setIsShowMainBanner,
  } = useMainStore();
  const targetRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        root: null, // 뷰포트를 기준으로 함
        rootMargin: '0px',
        threshold: 1, // 요소가 100% 보일 때 콜백 실행
      },
    );

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current);
      }
    };
  }, []);

  //이미지 배너 영역이 화면에 노출될때만 전송
  // 241115 : 긴급수정 - 홈으로 되돌아 왔을때 트래킹URL이 갱신되지 않아 예전 트래킹 URL을 계속 호출하는 문제를 후루꾸로 처리함. 배너 광고 쪽 정리가 필요함. 함정카드가 많다.
  useEffect(() => {
    if (
      checkAd.AdParameter.hasOwnProperty('sendTracking') &&
      isVisible &&
      !isCloseMainBanner
    ) {
      console.log('AD TEST hit ', bannerData.creatives[0]?.impressionUrls[0]);
      checkAd.AdParameter.sendTracking(bannerData.creatives[0]?.impressionUrls);
      checkAd.AdParameter.sendTracking(bannerData.creatives[0]?.viewableUrls);
      const adInfo = {
        func: 'sendImpressionUrls',
        ad_type: 'PC_MAIN_IMAGE',
        location: 'main',
      };
      LogApi.sendTestAdInfoLog(adInfo);
    }
  }, [isVisible, checkAd.AdParameter, bannerData.creatives]);

  return (
    <>
      {!isCloseMainBanner &&
        bannerData?.creatives?.map((banner, idx) => (
          <div
            key={idx}
            ref={targetRef}
            className={`${MainBannerCss.brandBanner} ${!isShowMainBanner ? MainBannerCss.close : ''}`}
            style={{background: banner.backgroundColor}}>
            <a
              href={banner.landingUrl}
              className={MainBannerCss.link}
              target='_blank'
              onClick={() => handleClickBanner(banner.clickUrls)}
              onError={(e) => handelErrorImage(e, banner.errorUrl)}>
              <GradientImage
                type='main'
                src={banner.thumbnailSrc}
                backgroundColor={banner.backgroundColor}
              />
              {banner.title}
            </a>
            <button
              type='button'
              className={MainBannerCss.close}
              onClick={() => {
                setIsCloseMainBanner(true);
                setIsShowMainBanner(false);
                if (checkAd.AdParameter.hasOwnProperty('sendTracking')) {
                  checkAd.AdParameter.sendTracking(banner.closeUrl);
                }
              }}>
              브랜드 배너 닫기
            </button>
          </div>
        ))}
      {isShowMainBanner && <div className={player.player_wrap}></div>}
    </>
  );
};

const VideoMainBannerComponent = ({bannerData, handleClickBanner}) => {
  const {
    checkAd,
    isShowMainBanner,
    isCloseMainBanner,
    setIsShowMainBanner,
    setIsCloseMainBanner,
  } = useMainStore();
  const targetRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        root: null, // 뷰포트를 기준으로 함
        rootMargin: '0px',
        threshold: 1, // 요소가 100% 보일 때 콜백 실행
      },
    );

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }
    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current);
      }
    };
  }, []);

  const handleClickVideoBanner = (banner) => {
    handleClickBanner(banner.clickUrls);
    if (banner.vast) {
      const subVastInfo = VastInfo.getVastInfo(banner.vast);
      handleClickBanner(subVastInfo.videoClicks.clickTracking);
    }
  };

  //이미지 배너 영역이 화면에 노출될때만 전송
  // 241115 : 긴급수정 - 홈으로 되돌아 왔을때 트래킹URL이 갱신되지 않아 예전 트래킹 URL을 계속 호출하는 문제를 후루꾸로 처리함. 배너 광고 쪽 정리가 필요함. 함정카드가 많다.
  useEffect(() => {
    if (
      checkAd.AdParameter.hasOwnProperty('sendTracking') &&
      isVisible &&
      !isCloseMainBanner
    ) {
      console.log('AD TEST hit  ', bannerData.creatives[0]?.impressionUrls[0]);
      checkAd.AdParameter.sendTracking(bannerData.creatives[0]?.impressionUrls);
      checkAd.AdParameter.sendTracking(bannerData.creatives[0]?.viewableUrls);
      const adInfo = {
        func: 'sendImpressionUrls',
        ad_type: 'PC_MAIN_VIDEO',
        location: 'main',
      };
      LogApi.sendTestAdInfoLog(adInfo);
    }
  }, [isVisible, checkAd.AdParameter, bannerData.creatives]);
  return (
    <>
      {!isCloseMainBanner &&
        bannerData?.creatives?.map((banner, idx) => (
          <div
            key={idx}
            ref={targetRef}
            className={`${MainBannerCss.brandBanner} ${!isShowMainBanner ? MainBannerCss.close : ''}`}
            style={{background: banner.backgroundColor}}>
            <a
              href={banner.landingUrl}
              className={MainBannerCss.link}
              target='_blank'
              onClick={() => handleClickVideoBanner(banner)}>
              <GradientImage
                type='main'
                src={banner.thumbnailSrc}
                backgroundColor={banner.backgroundColor}
              />
              {banner.title}
            </a>
            <div className={MainBannerCss.inner_w}>
              <div className={MainBannerCss.inner}>
                <div
                  className={`${MainBannerCss.player} ${banner.videoPlacement === 'LEFT' ? MainBannerCss.left : ''}`}>
                  <BannerAdPlayer
                    type='main'
                    adInfo={bannerData}
                    loop={true}
                    onError={(e) =>
                      handelErrorImage(
                        e,
                        banner.errorUrl,
                        banner?.alternativeImageUrl,
                      )
                    }
                  />
                </div>
              </div>
            </div>
            <button
              type='button'
              className={MainBannerCss.close}
              onClick={() => {
                setIsCloseMainBanner(true);
                setIsShowMainBanner(false);
                if (checkAd.AdParameter.hasOwnProperty('sendTracking')) {
                  checkAd.AdParameter.sendTracking(banner.closeUrl);
                }
              }}>
              브랜드 배너 닫기
            </button>
          </div>
        ))}
      {isShowMainBanner && <div className={player.player_wrap}></div>}
    </>
  );
};

const SubBannerComponent = ({
  isOpen,
  bannerData,
  handleClickClose,
  handelErrorImage,
  handleMouseLeave,
  handleMouseEnter,
  handleClickBanner,
}) => {
  if (bannerData.exist) {
    let ChildComponent;
    let isVideo = false;
    let isInfo = false;
    switch (bannerData.type) {
      case BANNER_RESPONSE_TYPE.PC_728_90_BANNER:
        return (
          <ImageSubBannerComponent
            bannerData={bannerData}
            handleClickBanner={handleClickBanner}
            handelErrorImage={handelErrorImage}
          />
        );
      case BANNER_RESPONSE_TYPE.PC_SUB_BANNER_EXPANDED_VIDEO:
      case BANNER_RESPONSE_TYPE.PC_MAIN_VIDEO_SUB:
        ChildComponent = ExpandImageToVideoSubBannerComponent;
        isVideo = true;
        break;
      case BANNER_RESPONSE_TYPE.PC_SUB_BANNER_EXPANDED_IMAGE:
      case BANNER_RESPONSE_TYPE.PC_MAIN_IMAGE_SUB:
        ChildComponent = ExpandImageToImageSubBannerComponent;
        break;
      case BANNER_RESPONSE_TYPE.PC_MAIN_VIDEO:
        ChildComponent = ExpandInfoToVideoSubBannerComponent;
        isVideo = true;
        isInfo = true;
        break;
      case BANNER_RESPONSE_TYPE.PC_MAIN_IMAGE:
        ChildComponent = ExpandInfoToImageSubBannerComponent;
        isInfo = true;
        break;
      default:
        return null;
    }

    return (
      <div
        className={`${subBannerCss.fullBanner} ${isOpen ? subBannerCss.open : ''} ${isInfo ? subBannerCss.brand : ''} ${isVideo && bannerData?.creatives[0]?.videoPlacement === 'RIGHT' ? subBannerCss.a_d_right : ''}`}>
        <ChildComponent
          isOpen={isOpen}
          bannerData={bannerData}
          handleClickBanner={handleClickBanner}
          handelErrorImage={handelErrorImage}
        />
        <ExpandButton
          isOpen={isOpen}
          onClick={() => handleClickClose(bannerData?.creatives[0]?.closeUrl)}
          handleMouseEnter={handleMouseEnter}
          handleMouseLeave={handleMouseLeave}
        />
      </div>
    );
  }
};

const ImageSubBannerComponent = ({
  bannerData,
  handelErrorImage,
  handleClickBanner,
}) => {
  return (
    <div className={`${subBannerCss.fullBanner}`}>
      {bannerData?.creatives?.map((banner, idx) => (
        <div key={idx}>
          {/* 축소 */}
          <FoldedImage
            backgroundColor={banner.backgroundColor}
            landingUrl={banner.landingUrl}
            onClick={() => handleClickBanner(banner.clickUrls)}
            imageUrl={banner.thumbnailSrc}
            onImageError={(e) => handelErrorImage(e, banner.errorUrl)}
          />
        </div>
      ))}
    </div>
  );
};

const ExpandImageToVideoSubBannerComponent = ({
  isOpen,
  bannerData,
  handleClickBanner,
  handelErrorImage,
}) => {
  return bannerData?.creatives?.map((banner, idx) => {
    let {thumbnailSrc, expandedBackgroundColor, expandedImageUrl} = banner;
    if (bannerData.type === BANNER_RESPONSE_TYPE.PC_MAIN_VIDEO_SUB) {
      expandedBackgroundColor = banner.backgroundColor;
      expandedImageUrl = thumbnailSrc;
      thumbnailSrc = banner.foldingImageUrl || thumbnailSrc;
    }
    return (
      <div key={idx}>
        {/* 축소 */}
        <FoldedImage
          backgroundColor={banner.backgroundColor}
          landingUrl={banner.landingUrl}
          onClick={() => handleClickBanner(banner.clickUrls)}
          imageUrl={thumbnailSrc}
          onImageError={(e) =>
            handelErrorImage(e, banner.errorUrl, banner?.alternativeImageUrl)
          }
        />
        {/* 펼침 */}
        <ExpandedVideo
          backgroundColor={expandedBackgroundColor}
          landingUrl={banner.landingUrl}
          imageUrl={expandedImageUrl}
          onClick={() => handleClickBanner(banner.clickUrls)}
          isOpen={isOpen}
          bannerData={bannerData}
          onPlayerError={(e) =>
            handelErrorImage(e, banner.errorUrl, banner?.alternativeImageUrl)
          }
        />
      </div>
    );
  });
};

const ExpandImageToImageSubBannerComponent = ({
  bannerData,
  handleClickBanner,
  handelErrorImage,
}) => {
  return bannerData.creatives?.map((banner, idx) => {
    let {thumbnailSrc, expandedBackgroundColor, expandedImageUrl} = banner;
    if (bannerData.type === BANNER_RESPONSE_TYPE.PC_MAIN_IMAGE_SUB) {
      expandedBackgroundColor = banner.backgroundColor;
      expandedImageUrl = thumbnailSrc;
      thumbnailSrc = banner.foldingImageUrl || thumbnailSrc;
    }
    return (
      <div key={idx}>
        {/* 축소 */}
        <FoldedImage
          backgroundColor={banner.backgroundColor}
          landingUrl={banner.landingUrl}
          onClick={() => handleClickBanner(banner.clickUrls)}
          imageUrl={thumbnailSrc}
          onImageError={(e) =>
            handelErrorImage(e, banner.errorUrl, banner?.alternativeImageUrl)
          }
        />
        {/* 펼침 */}
        <ExpandedImage
          backgroundColor={expandedBackgroundColor}
          landingUrl={banner.landingUrl}
          imageUrl={expandedImageUrl}
          onClick={() => handleClickBanner(banner.clickUrls)}
        />
      </div>
    );
  });
};

const ExpandInfoToVideoSubBannerComponent = ({
  isOpen,
  bannerData,
  handleClickBanner,
  handelErrorImage,
}) => {
  return bannerData?.creatives?.map((banner, idx) => (
    <div key={idx}>
      {/* 축소 */}
      <FoldedInfo banner={banner} handleClickBanner={handleClickBanner} />
      {/* 펼침 */}
      <ExpandedVideo
        backgroundColor={banner.backgroundColor}
        landingUrl={banner.landingUrl}
        imageUrl={banner.thumbnailSrc}
        onClick={() => handleClickBanner(banner.clickUrls)}
        isOpen={isOpen}
        bannerData={bannerData}
        onPlayerError={(e) =>
          handelErrorImage(e, banner.errorUrl, banner?.alternativeImageUrl)
        }
      />
    </div>
  ));
};

const ExpandInfoToImageSubBannerComponent = ({
  bannerData,
  handleClickBanner,
}) => {
  return bannerData.creatives?.map((banner, idx) => (
    <div key={idx}>
      {/* 축소 */}
      <FoldedInfo banner={banner} handleClickBanner={handleClickBanner} />
      {/* 펼침 */}
      <ExpandedImage
        backgroundColor={banner.backgroundColor}
        landingUrl={banner.landingUrl}
        imageUrl={banner.thumbnailSrc}
        onClick={() => handleClickBanner(banner.clickUrls)}
      />
    </div>
  ));
};

const FoldedImage = ({
  backgroundColor,
  landingUrl,
  onClick,
  imageUrl,
  onImageError,
}) => {
  return (
    <div
      className={subBannerCss.contractionImg}
      style={{background: backgroundColor}}>
      <a
        href={landingUrl}
        className={subBannerCss.link}
        target='_blank'
        onClick={onClick}>
        배너링크
      </a>
      <img src={imageUrl} alt='광고이미지' onError={onImageError} />
    </div>
  );
};

const FoldedInfo = ({banner, handleClickBanner}) => {
  return (
    <div
      className={subBannerCss.contractionImg}
      style={{background: 'rgba(227, 239, 246, 0.75)'}}>
      <a
        href={banner.landingUrl}
        className={subBannerCss.link}
        target='_blank'
        onClick={() => handleClickBanner(banner.clickUrls)}>
        배너링크
      </a>
      <div className={subBannerCss.folding_cont}>
        <div className={subBannerCss.inner}>
          <span className={subBannerCss.thumb}>
            <img
              src={banner.profileImageUrl}
              alt='프로필 이미지'
              loading='lazy'
            />
          </span>
          <div className={subBannerCss.textBox}>
            <em>{banner.userNickname}</em>
            <p>{banner.title}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ExpandedVideo = ({
  backgroundColor,
  landingUrl,
  imageUrl,
  onClick,
  isOpen,
  bannerData,
  onPlayerError,
}) => {
  return (
    <div
      className={subBannerCss.expandImg}
      style={{
        background: backgroundColor,
      }}>
      <a
        href={landingUrl}
        target='_blank'
        className={subBannerCss.link}
        onClick={onClick}>
        <GradientImage
          type='sub'
          src={imageUrl}
          backgroundColor={backgroundColor}
        />
        배너링크
      </a>
      {/* 플레이어 영역 */}
      <div className={subBannerCss.inner_w}>
        <div className={subBannerCss.inner}>
          {isOpen && (
            <div className={subBannerCss.player}>
              <BannerAdPlayer
                type='sub'
                adInfo={bannerData}
                loop={true}
                onError={onPlayerError}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ExpandedImage = ({backgroundColor, landingUrl, imageUrl, onClick}) => {
  return (
    <div
      className={subBannerCss.expandImg}
      style={{
        background: backgroundColor,
      }}>
      <a
        href={landingUrl}
        target='_blank'
        className={subBannerCss.link}
        onClick={onClick}>
        <GradientImage
          type='sub'
          src={imageUrl}
          backgroundColor={backgroundColor}
        />
        배너링크
      </a>
    </div>
  );
};

const GradientImage = ({type, src, backgroundColor}) => {
  const styles = type === 'main' ? MainBannerCss : subBannerCss;

  return (
    <div className={styles.img}>
      <img src={src} alt='' />
      <span
        className={styles.left}
        style={{
          background: `linear-gradient(90deg, ${backgroundColor} 14.12%, ${backgroundColor}99 39.46%, ${backgroundColor}42 57.86%, ${backgroundColor}00 80.27%)`,
        }}></span>
      <span
        className={styles.right}
        style={{
          background: `linear-gradient(-90deg, ${backgroundColor} 14.12%, ${backgroundColor}99 39.46%, ${backgroundColor}42 57.86%, ${backgroundColor}00 80.27%)`,
        }}></span>
    </div>
  );
};

const ExpandButton = ({
  isOpen,
  onClick,
  handleMouseEnter,
  handleMouseLeave,
}) => {
  return (
    <div className={subBannerCss.AdvertisingBtn}>
      {isOpen ? (
        <button type='button' className={subBannerCss.close} onClick={onClick}>
          <em></em>
          <i className={subBannerCss.text}>작게 보기</i>
        </button>
      ) : (
        <button
          type='button'
          className={subBannerCss.open}
          onClick={onClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          <em></em>
          <i className={subBannerCss.text}>자세히 보기</i>
        </button>
      )}
    </div>
  );
};
